import React, { useEffect, useRef, useState, useCallback } from "react";
import { Camera, Upload, CheckCircle2, AlertCircle, Loader2, Eye } from "lucide-react";
import { toast } from "sonner";
import { loadFaceModels, detectFace, detectFaceFromFile } from "@/lib/faceApi";

/**
 * Face capture component.
 *
 * Modes:
 *   mode="capture" — single frame with eyes-open guard (for enrollment)
 *   mode="match"   — continuous auto-match loop (no button)
 *
 * Props:
 *   onEmbedding(emb: number[128]) — fired when a clean face is captured/matched
 *   autoStart (bool) — open camera immediately
 */
export const FaceCapture = ({ mode = "capture", onEmbedding, autoStart = false, onError }) => {
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const loopRef = useRef(null);
    const submittedRef = useRef(false);

    const [loadingModels, setLoadingModels] = useState(true);
    const [cameraOpen, setCameraOpen] = useState(autoStart);
    const [cameraReady, setCameraReady] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [hint, setHint] = useState(null); // { tone: 'info'|'warn'|'ok', text }
    const [error, setError] = useState(null);

    // Lazy-load models
    useEffect(() => {
        loadFaceModels()
            .then(() => setLoadingModels(false))
            .catch((e) => {
                setLoadingModels(false);
                setError("Failed to load face models. Check your connection and reload.");
                onError?.(e);
            });
    }, [onError]);

    const stopCamera = useCallback(() => {
        if (loopRef.current) {
            clearTimeout(loopRef.current);
            loopRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        setCameraReady(false);
    }, []);

    const startCamera = useCallback(async () => {
        setError(null);
        setCameraOpen(true);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
                audio: false,
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
                setCameraReady(true);
            }
        } catch (e) {
            setCameraReady(false);
            const msg = e?.name === "NotAllowedError"
                ? "Camera permission denied. Allow access in your browser settings, or use 'Upload photo' below."
                : "Couldn't open the camera. Use 'Upload photo' instead.";
            setError(msg);
            onError?.(e);
        }
    }, [onError]);

    useEffect(() => () => stopCamera(), [stopCamera]);

    // Auto-start
    useEffect(() => {
        if (autoStart && !loadingModels && !cameraReady && !error) {
            startCamera();
        }
    }, [autoStart, loadingModels, cameraReady, error, startCamera]);

    // Single capture handler (mode="capture")
    const captureSingleFrame = useCallback(async () => {
        if (!videoRef.current || processing || submittedRef.current) return;
        setProcessing(true);
        try {
            const det = await detectFace(videoRef.current);
            if (!det) {
                setHint({ tone: "warn", text: "No face detected. Center your face in the oval." });
                return;
            }
            if (!det.eyesOpen) {
                setHint({ tone: "warn", text: "Please keep both eyes open and look at the camera." });
                return;
            }
            setHint({ tone: "ok", text: "Captured." });
            submittedRef.current = true;
            stopCamera();
            onEmbedding?.(det.embedding);
        } catch (e) {
            toast.error("Face detection failed. Try again.");
            onError?.(e);
        } finally {
            setProcessing(false);
        }
    }, [processing, stopCamera, onEmbedding, onError]);

    // Auto-match loop (mode="match")
    useEffect(() => {
        if (mode !== "match" || !cameraReady || submittedRef.current) return;

        let cancelled = false;
        const tick = async () => {
            if (cancelled || submittedRef.current) return;
            try {
                const det = await detectFace(videoRef.current);
                if (cancelled || submittedRef.current) return;
                if (!det) {
                    setHint({ tone: "info", text: "Looking for your face…" });
                } else if (!det.eyesOpen) {
                    setHint({ tone: "warn", text: "Eyes detected closed — open your eyes." });
                } else {
                    setHint({ tone: "ok", text: "Matching…" });
                    submittedRef.current = true;
                    stopCamera();
                    onEmbedding?.(det.embedding);
                    return;
                }
            } catch {
                /* swallow */
            }
            loopRef.current = setTimeout(tick, 700);
        };
        tick();
        return () => {
            cancelled = true;
            if (loopRef.current) clearTimeout(loopRef.current);
        };
    }, [mode, cameraReady, onEmbedding, stopCamera]);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please choose an image file.");
            return;
        }
        setProcessing(true);
        try {
            const det = await detectFaceFromFile(file);
            if (!det) {
                toast.error("No face found. Try a clear front-facing photo.");
                return;
            }
            if (!det.eyesOpen) {
                toast.error("Eyes appear closed in this photo. Try one with eyes open.");
                return;
            }
            submittedRef.current = true;
            onEmbedding?.(det.embedding);
        } catch (err) {
            toast.error("Could not process this image.");
            onError?.(err);
        } finally {
            setProcessing(false);
            e.target.value = "";
        }
    };

    if (loadingModels) {
        return (
            <div className="flex items-center justify-center gap-2 p-6 text-slate-500" data-testid="face-loading">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Loading face models…</span>
            </div>
        );
    }

    const hintColor = {
        info: "text-slate-700 bg-white/90",
        warn: "text-amber-800 bg-amber-50/95 border-amber-200",
        ok: "text-green-700 bg-green-50/95 border-green-200",
    }[hint?.tone || "info"];

    return (
        <div className="space-y-3" data-testid="face-capture">
            {!cameraOpen && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                        onClick={startCamera}
                        disabled={processing}
                        data-testid="face-capture-camera"
                        className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-lg bg-[#2EB4AC] hover:bg-[#25908A] text-white font-medium transition-colors disabled:opacity-60"
                    >
                        <Camera className="h-5 w-5" /> Use camera
                    </button>
                    <label className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-lg border border-slate-200 bg-white hover:border-[#2EB4AC] hover:text-[#2EB4AC] text-slate-700 font-medium transition-colors cursor-pointer" data-testid="face-capture-upload-label">
                        <Upload className="h-5 w-5" /> Upload photo
                        <input type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="face-capture-upload" />
                    </label>
                </div>
            )}

            {cameraOpen && (
                <div className="space-y-3">
                    <div className="relative aspect-[4/3] max-w-md mx-auto rounded-xl overflow-hidden bg-slate-900 border border-slate-200 shadow-inner">
                        <video
                            ref={videoRef}
                            data-testid="face-capture-video"
                            playsInline
                            muted
                            aria-label="Live camera preview"
                            className="absolute inset-0 h-full w-full object-cover scale-x-[-1]"
                        />
                        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                            <div className={`border-4 rounded-[50%] transition-colors ${hint?.tone === "ok" ? "border-green-400" : hint?.tone === "warn" ? "border-amber-400" : "border-white/70"}`} style={{ width: "60%", height: "75%" }} />
                        </div>
                        {hint && (
                            <div className={`absolute bottom-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 backdrop-blur px-3 py-1 rounded-full text-xs font-medium border shadow ${hintColor}`} data-testid="face-capture-hint">
                                {hint.tone === "ok" ? <CheckCircle2 className="h-3.5 w-3.5" /> : hint.tone === "warn" ? <AlertCircle className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                                <span>{hint.text}</span>
                            </div>
                        )}
                        {processing && mode !== "match" && (
                            <div className="absolute inset-0 flex items-center justify-center bg-slate-900/30 text-white pointer-events-none">
                                <Loader2 className="h-8 w-8 animate-spin" />
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-center gap-2 flex-wrap">
                        {mode === "capture" && (
                            <button
                                onClick={captureSingleFrame}
                                disabled={!cameraReady || processing}
                                data-testid="face-capture-shoot"
                                className="inline-flex items-center gap-2 h-12 px-6 rounded-lg bg-[#2EB4AC] hover:bg-[#25908A] text-white font-medium transition-colors disabled:opacity-60"
                            >
                                <Camera className="h-5 w-5" /> Capture
                            </button>
                        )}
                        <button
                            onClick={() => { stopCamera(); setCameraOpen(false); submittedRef.current = false; setHint(null); }}
                            data-testid="face-capture-cancel"
                            className="inline-flex items-center gap-2 h-12 px-4 rounded-lg text-slate-500 hover:text-slate-900 font-medium"
                        >
                            Cancel
                        </button>
                    </div>

                    <p className="text-xs text-slate-500 text-center max-w-md mx-auto">
                        {mode === "match"
                            ? "Look at the camera with both eyes open — we'll match automatically."
                            : "Center your face in the oval, eyes open, then tap Capture."}
                    </p>
                </div>
            )}

            {error && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="face-capture-error">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{error}</span>
                </div>
            )}
        </div>
    );
};

export default FaceCapture;
